import React from 'react';
import { Sparkles, Upload } from 'lucide-react';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import { error } from 'core/components/Notify';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { extractUploadedUrl } from 'features/DigitalMarketing/utils/apiResponse';
import type { BlogForm } from '../blogUtils';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMText,
  DMTextarea,
} from '../../../components/Primitives';

const maxBlogImageSize = 15 * 1024 * 1024;
const allowedBlogImageTypes = ['image/png', 'image/jpg', 'image/jpeg'];

interface BlogFormViewProps {
  isEditMode: boolean;
  blogForm: BlogForm;
  setBlogForm: React.Dispatch<React.SetStateAction<BlogForm>>;
  handleGenerateText: () => void;
  isGeneratingAIText: boolean;
  hasAIGeneratedText: boolean;
  handleGenerateImage: () => void;
  isGeneratingAIImage: boolean;
  hasAIGeneratedImage: boolean;
  isVerifyingImage: boolean;
  verificationError: boolean;
  isUploadingImage: boolean;
  setIsUploadingImage: (val: boolean) => void;
  isEnhancingImage?: boolean;
  handleEnhanceImage?: () => void;
  onPreview: () => void;
}

export const BlogFormView = ({
  isEditMode,
  blogForm,
  setBlogForm,
  handleGenerateText,
  isGeneratingAIText,
  hasAIGeneratedText,
  handleGenerateImage,
  isGeneratingAIImage,
  hasAIGeneratedImage,
  isVerifyingImage,
  verificationError,
  isUploadingImage,
  setIsUploadingImage,
  isEnhancingImage = false,
  handleEnhanceImage,
  onPreview,
}: BlogFormViewProps) => {
  const authData = useAuth();
  const isImageActionInProgress =
    isGeneratingAIImage ||
    isUploadingImage ||
    isVerifyingImage ||
    isEnhancingImage;
  const RequiredDot = () => (
    <DMText as="span" className="ml-1 text-red-500">
      *
    </DMText>
  );

  return (
    <DMDiv className="flex-1">
      <DigitalMarketingBreadcrumb
        className="mb-6"
        items={[
          { label: 'Blogs', path: '/digital-marketing/blog' },
          { label: isEditMode ? 'Edit Blog' : 'Create Blog' },
        ]}
      />

      <DMDiv className="mb-8">
        <DMText as="h1" className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Blog' : 'Create Blog'}
        </DMText>
        <DMText className="text-gray-500">
          Publish impactful blogs to strengthen your school's brand, improve
          online reach, and engage parents effectively
        </DMText>
      </DMDiv>

      <DMDiv className="space-y-6">
        <DMDiv className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DMDiv>
            <DMLabel className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-700">
              Title
              <RequiredDot />
            </DMLabel>
            <DMInput
              type="text"
              placeholder="Enter Title"
              value={blogForm.title}
              onChange={e =>
                setBlogForm({ ...blogForm, title: e.target.value })
              }
              className="w-full rounded-xl border border-gray-200 bg-white p-4 text-gray-900 focus:border-[#5E4DE0] focus:outline-none focus:ring-1 focus:ring-[#5E4DE0]"
            />
          </DMDiv>

          <DMDiv>
            <DMLabel className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-700">
              Description
            </DMLabel>
            <DMInput
              type="text"
              placeholder="Enter Description"
              value={blogForm.description}
              onChange={e =>
                setBlogForm({ ...blogForm, description: e.target.value })
              }
              className="w-full rounded-xl border border-gray-200 bg-white p-4 text-gray-900 focus:border-[#5E4DE0] focus:outline-none focus:ring-1 focus:ring-[#5E4DE0]"
            />
          </DMDiv>
        </DMDiv>

        <DMDiv className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DMDiv>
            <DMLabel className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-700">
              Blog Content
              <RequiredDot />
            </DMLabel>
            <DMDiv className="flex h-[300px] flex-col rounded-xl border border-gray-200 bg-white p-6 gap-4">
              <DMTextarea
                value={blogForm.content}
                onChange={e =>
                  setBlogForm({ ...blogForm, content: e.target.value })
                }
                placeholder="Enter Blog Content or Click the button below to generate it using AI."
                className="flex-1 w-full overflow-auto text-gray-700 leading-relaxed bg-transparent resize-none focus:outline-none"
              />
              <DMDiv className="mt-4 flex justify-center">
                <DMButton
                  onClick={handleGenerateText}
                  disabled={isGeneratingAIText}
                  className="flex items-center gap-2 rounded-full bg-[#5E4DE0] px-6 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] disabled:opacity-50"
                >
                  <Sparkles size={18} />
                  {isGeneratingAIText
                    ? 'Generating...'
                    : hasAIGeneratedText
                    ? 'Regenerate'
                    : 'Generate with AI'}
                </DMButton>
              </DMDiv>
            </DMDiv>
          </DMDiv>

          <DMDiv>
            <DMLabel className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-700">
              Featured Image
              <RequiredDot />
            </DMLabel>
            <DMDiv className="flex h-[300px] flex-col rounded-xl border border-gray-200 bg-white p-6">
              {blogForm.image ||
              isUploadingImage ||
              isGeneratingAIImage ||
              isVerifyingImage ||
              verificationError ? (
                <DMDiv className="mb-4 flex-1 overflow-hidden rounded-lg relative bg-gray-50">
                  {!!blogForm.image && !isVerifyingImage && (
                    <DMImage
                      key={`img-${blogForm.image}`}
                      src={blogForm.image}
                      alt="Featured"
                      onError={e => {
                        const target = e.currentTarget;
                        if (
                          !isGeneratingAIImage &&
                          !isUploadingImage &&
                          !isVerifyingImage
                        ) {
                          target.onerror = null;
                          target.src =
                            'https://placehold.co/600x400/E1DCFD/5E4DE0?text=Image+Load+Failed';
                        }
                      }}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {verificationError && !blogForm.image && (
                    <DMDiv className="flex h-full flex-col items-center justify-center p-4">
                      <DMImage
                        src="https://placehold.co/600x400/E1DCFD/5E4DE0?text=Image+Readiness+Timeout"
                        alt="Error"
                        className="mb-2 h-20 opacity-50"
                      />
                      <DMText className="text-xs text-red-500 font-medium">
                        Image generation timed out. Please try again.
                      </DMText>
                    </DMDiv>
                  )}
                  {(isUploadingImage ||
                    isGeneratingAIImage ||
                    isVerifyingImage ||
                    isEnhancingImage) && (
                    <DMDiv className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/50 backdrop-blur-[2px] z-10">
                      <SetupLoader height="h-10" />
                      <DMText className="mt-2 text-xs font-medium text-[#5E4DE0]">
                        {isVerifyingImage
                          ? 'Verifying AI image readiness...'
                          : isEnhancingImage
                          ? 'AI is enhancing your image...'
                          : isGeneratingAIImage
                          ? 'AI is generating your image...'
                          : 'Uploading...'}
                      </DMText>
                    </DMDiv>
                  )}

                  {!!blogForm.image &&
                    !isImageActionInProgress &&
                    handleEnhanceImage && (
                      <DMButton
                        onClick={e => {
                          e.preventDefault();
                          handleEnhanceImage();
                        }}
                        title="Enhance Image"
                        className="absolute top-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#5E4DE0] shadow-lg transition-all hover:scale-110 hover:bg-white active:scale-95 group"
                      >
                        <Sparkles
                          size={20}
                          className="group-hover:animate-pulse"
                        />
                        <DMDiv className="absolute -inset-1 rounded-full bg-[#5E4DE0]/20 animate-ping opacity-0 group-hover:opacity-100" />
                      </DMButton>
                    )}
                </DMDiv>
              ) : (
                <DMDiv className="flex flex-1 flex-col items-center justify-center text-center text-gray-500">
                  <DMText className="mb-6">
                    Generate Image Using SOOMUCH.AI.
                  </DMText>
                </DMDiv>
              )}

              <DMDiv className="flex gap-4">
                <DMButton
                  onClick={handleGenerateImage}
                  disabled={isImageActionInProgress}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#5E4DE0] px-4 py-3 font-medium text-[#5E4DE0] transition-colors hover:bg-[#E1DCFD] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles size={18} />
                  {isGeneratingAIImage
                    ? 'Generating...'
                    : hasAIGeneratedImage
                    ? 'Regenerate'
                    : 'Generate Image'}
                </DMButton>
                <DMLabel
                  className={`flex flex-1 items-center justify-center gap-2 rounded-full border border-[#5E4DE0] px-4 py-3 font-medium text-[#5E4DE0] transition-colors ${
                    isImageActionInProgress
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-[#E1DCFD] cursor-pointer'
                  }`}
                >
                  <Upload size={18} />{' '}
                  {isUploadingImage ? 'Uploading...' : 'Upload Image'}
                  <DMInput
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isImageActionInProgress}
                    onChange={async e => {
                      const input = e.currentTarget;
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!allowedBlogImageTypes.includes(file.type)) {
                          error(
                            'Only PNG, JPG, and JPEG image files are allowed.',
                            true
                          );
                          input.value = '';
                          return;
                        }

                        if (file.size > maxBlogImageSize) {
                          error('Image must be 15MB or less.', true);
                          input.value = '';
                          return;
                        }

                        setIsUploadingImage(true);
                        try {
                          const formData = new FormData();
                          formData.append('dest', 'DM-CONTENT');
                          formData.append(
                            'branch_id',
                            authData?.BRANCH_ID as any
                          );
                          formData.append(
                            'database',
                            authData?.DATABASE as any
                          );
                          formData.append('files', file);

                          const s3Url: any = await apiFileUpload(
                            formData,
                            'upload',
                            '',
                            '',
                            true,
                            false,
                            true
                          );

                          const finalUrl = extractUploadedUrl(s3Url);

                          if (finalUrl) {
                            setBlogForm(prev => ({ ...prev, image: finalUrl }));
                          } else {
                            error('Unable to read uploaded image URL.', true);
                          }
                        } catch (err) {
                          error(
                            'Failed to upload image. Please try again.',
                            true
                          );
                        } finally {
                          setIsUploadingImage(false);
                          input.value = '';
                        }
                      }
                    }}
                  />
                </DMLabel>
              </DMDiv>
            </DMDiv>
          </DMDiv>
        </DMDiv>

        <DMDiv className="flex justify-end pt-2 gap-4">
          <DMButton
            onClick={onPreview}
            disabled={!blogForm.title || !blogForm.content || !blogForm.image}
            className="rounded-full bg-[#5E4DE0] px-12 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] disabled:opacity-50"
          >
            Preview
          </DMButton>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};
